/* ============================================================
   kw-predictive-search.js — predictive search for the Kasuwa theme.

   Enhances every <predictive-search> element rendered by
   snippets/kw-predictive-search.liquid:
     - debounced queries to /search/suggest.json
       (products, pages, and articles)
     - grouped results with product thumbnails and prices
     - keyboard support: ArrowUp/ArrowDown move through results,
       Enter opens the highlighted result, Escape closes
     - aria-expanded / aria-activedescendant on the combobox input,
       plus a polite live region announcing result counts

   Dependency-free vanilla JS. Safe to load more than once:
   each <predictive-search> element is initialized only once.
   ============================================================ */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 300;
  var MAX_PRODUCTS = 6;
  var MAX_PAGES = 4;
  var MAX_ARTICLES = 4;

  var instanceCounter = 0;

  /* Escape a string for safe insertion into HTML. */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Format integer cents with the shop money format passed from Liquid. */
  function formatMoney(cents, format) {
    var amount = Number(cents) / 100;
    if (!isFinite(amount)) return '';
    var withDecimals = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals);
  }

  function buildSuggestUrl(query) {
    return '/search/suggest.json?' + [
      'q=' + encodeURIComponent(query),
      'resources[type]=product,page,article',
      'resources[limit]=6',
      'resources[limit_scope]=each',
      'resources[options][unavailable_products]=hide',
      'resources[options][fields]=title,product_type,vendor'
    ].join('&');
  }

  function productImageUrl(image, width) {
    if (!image || !image.url) return '';
    var separator = image.url.indexOf('?') === -1 ? '?' : '&';
    return image.url + separator + 'width=' + (width || 160);
  }

  function initPredictiveSearch(root) {
    if (root.dataset.kwPsReady === '1') return;
    root.dataset.kwPsReady = '1';

    var input = root.querySelector('input[type="search"]');
    var resultsBox = root.querySelector('[data-kw-ps-results]');
    var statusBox = root.querySelector('[data-kw-ps-status]');
    if (!input || !resultsBox) return;

    instanceCounter += 1;
    var uid = 'kwps' + instanceCounter;

    var strings = {
      products: root.getAttribute('data-products-heading') || 'Products',
      pages: root.getAttribute('data-pages-heading') || 'Pages',
      articles: root.getAttribute('data-articles-heading') || 'Articles',
      viewAll: root.getAttribute('data-view-all-text') || 'View all results',
      noResults: root.getAttribute('data-no-results-text') || 'No suggestions found.',
      loading: root.getAttribute('data-loading-text') || 'Loading search suggestions.',
      count: root.getAttribute('data-count-text') || '{count} suggestions'
    };
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var searchUrl = root.getAttribute('data-search-url') || '/search';

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', resultsBox.id);
    input.setAttribute('aria-expanded', 'false');

    var debounceTimer = null;
    var aborter = null;
    var lastQuery = '';
    var optionEls = [];
    var activeIndex = -1;

    function setStatus(message) {
      if (statusBox) statusBox.textContent = message;
    }

    function open() {
      resultsBox.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      resultsBox.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      optionEls = [];
      activeIndex = -1;
    }

    function clearPending() {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (aborter) {
        aborter.abort();
        aborter = null;
      }
    }

    function setActive(index) {
      optionEls.forEach(function (el, i) {
        var selected = i === index;
        el.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          input.setAttribute('aria-activedescendant', el.id);
          if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
          }
        }
      });
      activeIndex = index;
      if (index === -1) input.removeAttribute('aria-activedescendant');
    }

    function renderProduct(product, optionId) {
      var price = formatMoney(product.price, moneyFormat);
      var compare = '';
      if (Number(product.compare_at_price) > Number(product.price)) {
        compare = ' <s class="kw-ps__compare">' + esc(formatMoney(product.compare_at_price, moneyFormat)) + '</s>';
      }
      var thumb = product.featured_image && product.featured_image.url
        ? '<img class="kw-ps__thumb" src="' + esc(productImageUrl(product.featured_image, 160)) + '" alt="" width="56" height="56" loading="lazy">'
        : '<span class="kw-ps__thumb kw-ps__thumb--empty" aria-hidden="true"></span>';
      var vendor = product.vendor ? esc(product.vendor) : '';
      return '<li class="kw-ps__option" id="' + optionId + '" role="option" aria-selected="false">' +
        '<a class="kw-ps__link" href="' + esc(product.url) + '" tabindex="-1">' +
        thumb +
        '<span class="kw-ps__info">' +
        '<span class="kw-ps__title">' + esc(product.title) + '</span>' +
        '<span class="kw-ps__meta">' + vendor + (vendor && price ? ' &middot; ' : '') + esc(price) + compare + '</span>' +
        '</span></a></li>';
    }

    function renderSimpleRow(item, optionId) {
      return '<li class="kw-ps__option" id="' + optionId + '" role="option" aria-selected="false">' +
        '<a class="kw-ps__link" href="' + esc(item.url) + '" tabindex="-1">' +
        '<span class="kw-ps__info"><span class="kw-ps__title">' + esc(item.title) + '</span></span>' +
        '</a></li>';
    }

    function renderGroup(heading, items, renderer, groupIndex) {
      if (!items || items.length === 0) return '';
      var headingId = uid + '-heading-' + groupIndex;
      var html = '<div class="kw-ps__group" role="group" aria-labelledby="' + headingId + '">';
      html += '<p class="kw-ps__heading" id="' + headingId + '">' + esc(heading) + '</p>';
      html += '<ul class="kw-ps__list">';
      items.forEach(function (item, i) {
        html += renderer(item, uid + '-opt-' + groupIndex + '-' + i);
      });
      html += '</ul></div>';
      return html;
    }

    function render(data, query) {
      var results = (data && data.resources && data.resources.results) || {};
      var products = (results.products || []).slice(0, MAX_PRODUCTS);
      var pages = (results.pages || []).slice(0, MAX_PAGES);
      var articles = (results.articles || []).slice(0, MAX_ARTICLES);
      var total = products.length + pages.length + articles.length;

      var html = '';
      html += renderGroup(strings.products, products, renderProduct, 0);
      html += renderGroup(strings.pages, pages, renderSimpleRow, 1);
      html += renderGroup(strings.articles, articles, renderSimpleRow, 2);

      if (total === 0) {
        html = '<p class="kw-ps__empty">' + esc(strings.noResults) + '</p>';
      }

      html += '<a class="kw-ps__view-all" href="' + esc(searchUrl) + '?q=' + encodeURIComponent(query) + '" tabindex="-1">' +
        esc(strings.viewAll) + ' <span aria-hidden="true">&rarr;</span></a>';

      resultsBox.innerHTML = html;
      optionEls = Array.prototype.slice.call(resultsBox.querySelectorAll('.kw-ps__option'));
      setActive(-1);
      open();
      setStatus(total === 0 ? strings.noResults : strings.count.replace('{count}', String(total)));
    }

    function fetchSuggestions(query) {
      lastQuery = query;
      resultsBox.innerHTML = '<p class="kw-ps__loading">' + esc(strings.loading) + '</p>';
      open();

      if (typeof AbortController !== 'undefined') {
        aborter = new AbortController();
      }

      fetch(buildSuggestUrl(query), {
        headers: { Accept: 'application/json' },
        signal: aborter ? aborter.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error('predictive search request failed');
          return response.json();
        })
        .then(function (data) {
          if (input.value.trim() !== lastQuery) return; // stale response
          render(data, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          close();
          setStatus('');
        });
    }

    input.addEventListener('input', function () {
      clearPending();
      var query = input.value.trim();
      if (query.length < MIN_CHARS) {
        close();
        setStatus('');
        return;
      }
      debounceTimer = setTimeout(function () {
        debounceTimer = null;
        fetchSuggestions(query);
      }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        clearPending();
        close();
        setStatus('');
        return;
      }
      if (resultsBox.hidden || optionEls.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex >= optionEls.length - 1 ? 0 : activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex <= 0 ? optionEls.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter') {
        if (activeIndex >= 0 && optionEls[activeIndex]) {
          event.preventDefault();
          var link = optionEls[activeIndex].querySelector('a');
          if (link) link.click();
        }
      }
    });

    input.addEventListener('blur', function () {
      /* Give a click on a result time to land before closing. */
      setTimeout(function () {
        if (!root.contains(document.activeElement)) close();
      }, 150);
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) close();
    });
  }

  function initAll() {
    var roots = document.querySelectorAll('predictive-search');
    Array.prototype.forEach.call(roots, initPredictiveSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
